import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAsset, getMinistry, createAsset, updateAsset } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxes,
  faSave,
  faSpinner,
  faArrowLeft,
  faCamera,
  faTrash,
  faTag,
  faBarcode,
  faCubes,
  faCalendarAlt,
  faDollarSign,
  faFileAlt,
  faCloudUploadAlt,
  faCheckCircle,
  faInfoCircle,
  faImages,
  faMapMarkerAlt,
  faFolderOpen
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import ProgressBar from '../components/ProgressBar';
import './AssetForm.css';

const AssetForm = ({ selectedYear }) => {
  const { id: ministryId, assetId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEditing = !!assetId;

  const [ministry, setMinistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [formData, setFormData] = useState({
    description: '',
    serialNumber: '',
    quantity: 1,
    acquisitionDate: '',
    value: '',
    location: ''
  });

  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);

  useEffect(() => {
    loadData();
  }, [ministryId, assetId, selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const ministryRes = await getMinistry(ministryId, selectedYear);
      setMinistry(ministryRes.data);

      if (isEditing) {
        const assetRes = await getAsset(assetId);
        const asset = assetRes.data;
        setFormData({
          description: asset.description || '',
          serialNumber: asset.serialNumber || '',
          quantity: asset.quantity || 1,
          acquisitionDate: asset.acquisitionDate ? asset.acquisitionDate.split('T')[0] : '',
          value: asset.value || '',
          location: asset.location || ''
        });
        setExistingPhotos(asset.photos || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = existingPhotos.length + newPhotos.length + files.length;
    
    if (totalPhotos > 5) {
      toast.warn('Máximo de 5 fotos permitidas.');
      return;
    }

    setNewPhotos(prev => [...prev, ...files]);

    // Create previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotosPreviews(prev => [...prev, { url: e.target.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeExistingPhoto = (index) => {
    setExistingPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeNewPhoto = (index) => {
    setNewPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotosPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      toast.warn('A descrição é obrigatória.');
      return;
    }

    try {
      setSaving(true);
      setUploadProgress(0);

      const submitData = new FormData();
      submitData.append('description', formData.description.trim());
      submitData.append('ministry', ministryId);
      submitData.append('serialNumber', formData.serialNumber.trim());
      submitData.append('quantity', formData.quantity || 1);
      if (formData.acquisitionDate) {
        submitData.append('acquisitionDate', formData.acquisitionDate);
      }
      submitData.append('value', formData.value || 0);
      submitData.append('location', formData.location.trim());

      // Existing photos (for edit)
      if (isEditing) {
        submitData.append('existingPhotos', JSON.stringify(existingPhotos));
      }

      // New photos
      newPhotos.forEach(photo => {
        submitData.append('photos', photo);
      });

      const onProgress = (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      };

      if (isEditing) {
        await updateAsset(assetId, submitData, onProgress);
        toast.success('Bem atualizado com sucesso!');
      } else {
        await createAsset(submitData, onProgress);
        toast.success('Bem cadastrado com sucesso!');
      }

      navigate(`/ministry/${ministryId}/assets`);
    } catch (error) {
      console.error('Erro ao salvar bem:', error);
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;

      if (status === 409) {
        // Conflito de código — orientar a tentar novamente
        toast.warn(serverMessage || 'Houve um conflito ao gerar o código. Por favor, tente novamente.');
      } else if (status === 403) {
        toast.error(serverMessage || 'Você não tem permissão para realizar esta ação.');
      } else if (status === 413) {
        toast.error('As fotos enviadas excedem o tamanho máximo permitido (10 MB cada).');
      } else {
        toast.error(serverMessage || 'Erro ao salvar bem. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
        Carregando...
      </div>
    );
  }

  return (
    <div className="asset-form-page">
      <div className="container">
        {/* Header */}
        <div className="asset-form-header">
          <div className="asset-form-header-left">
            <button onClick={() => navigate(`/ministry/${ministryId}/assets`)} className="btn-back">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h1>
                <FontAwesomeIcon icon={faBoxes} className="header-icon" />
                {isEditing ? 'Editar Bem' : 'Cadastrar Novo Bem'}
              </h1>
              <p className="form-subtitle">{ministry?.name}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="asset-form-card">
          {/* Photos section */}
          <div className="form-section">
            <div className="section-title-row">
              <h2 className="section-title">
                <FontAwesomeIcon icon={faCamera} />
                Fotos do Bem
              </h2>
              <span className="photo-counter" title="Quantidade de fotos adicionadas">
                <FontAwesomeIcon icon={faImages} />
                {existingPhotos.length + newPhotos.length} / 5
              </span>
            </div>
            <p className="section-description">
              Adicione fotos para facilitar a identificação visual do bem. Aceita JPG, PNG ou WEBP.
            </p>

            <div className="photos-area">
              {/* Existing photos */}
              {existingPhotos.map((photo, idx) => (
                <div key={`existing-${idx}`} className="photo-preview">
                  <img src={photo} alt={`Foto ${idx + 1}`} />
                  <button 
                    type="button" 
                    className="photo-remove" 
                    onClick={() => removeExistingPhoto(idx)}
                    disabled={saving}
                    title="Remover esta foto"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}

              {/* New photo previews */}
              {photosPreviews.map((preview, idx) => (
                <div key={`new-${idx}`} className="photo-preview">
                  <img src={preview.url} alt={preview.name} />
                  <button 
                    type="button" 
                    className="photo-remove" 
                    onClick={() => removeNewPhoto(idx)}
                    disabled={saving}
                    title="Remover esta foto"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}

              {/* Upload buttons */}
              {(existingPhotos.length + newPhotos.length) < 5 && (
                <>
                  <label className="photo-upload-btn photo-btn-camera" title="Abrir câmera para tirar foto do bem">
                    <FontAwesomeIcon icon={faCamera} />
                    <span>Tirar Foto</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoSelect}
                      style={{ display: 'none' }}
                      disabled={saving}
                    />
                  </label>
                  <label className="photo-upload-btn photo-btn-file" title="Selecionar imagem da galeria ou arquivos do dispositivo">
                    <FontAwesomeIcon icon={faFolderOpen} />
                    <span>Anexar Imagem</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoSelect}
                      style={{ display: 'none' }}
                      disabled={saving}
                    />
                  </label>
                </>
              )}

              {/* Empty state */}
              {existingPhotos.length === 0 && newPhotos.length === 0 && (
                <p className="photos-empty-hint">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  Nenhuma foto adicionada. As fotos são opcionais, mas ajudam na identificação do bem.
                </p>
              )}
            </div>
          </div>

          {/* Details section */}
          <div className="form-section">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faFileAlt} />
              Informações do Bem
            </h2>
            <p className="section-description">
              Preencha os dados do bem patrimonial. O código será gerado automaticamente.
            </p>

            <div className="form-grid">
              <div className="form-group form-full">
                <label className="form-label">
                  <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: '6px' }} />
                  Descrição
                  <span className="label-required">*</span>
                  <span className="info-hint" data-tip="Descreva o bem de forma clara para fácil identificação no inventário.">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </label>
                <input
                  type="text"
                  name="description"
                  className="form-input"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Ex: Mesa de escritório, Caixa de som JBL Partybox..."
                  required
                  disabled={saving}
                />
                <span className="field-hint">Campo obrigatório. Seja específico para facilitar a busca.</span>
              </div>

              <div className="form-group form-full">
                <label className="form-label">
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '6px' }} />
                  Localização
                  <span className="info-hint" data-tip="Informe onde o bem está fisicamente guardado ou instalado dentro da igreja.">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </label>
                <input
                  type="text"
                  name="location"
                  className="form-input"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="Ex: Sala de som, Cozinha, Depósito, Salão principal..."
                  disabled={saving}
                />
                <span className="field-hint">Opcional. Em qual espaço físico o bem se encontra — sala, ambiente ou área da igreja.</span>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faTag} style={{ marginRight: '6px' }} />
                  Nº da Etiqueta
                  <span className="info-hint" data-tip="Número da etiqueta de patrimônio colada fisicamente no bem, se houver.">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </label>
                <input
                  type="text"
                  name="serialNumber"
                  className="form-input"
                  value={formData.serialNumber}
                  onChange={handleChange}
                  placeholder="Ex: ET-001, SN-12345..."
                  disabled={saving}
                />
                <span className="field-hint">Opcional. Número de identificação física do bem.</span>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCubes} style={{ marginRight: '6px' }} />
                  Quantidade
                  <span className="info-hint" data-tip="Quantidade de itens iguais. Use 1 para itens únicos.">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  className="form-input"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px' }} />
                  Data de Aquisição
                  <span className="info-hint" data-tip="Data em que o bem foi adquirido ou recebido pela igreja.">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </label>
                <input
                  type="date"
                  name="acquisitionDate"
                  className="form-input"
                  value={formData.acquisitionDate}
                  onChange={handleChange}
                  disabled={saving}
                />
                <span className="field-hint">Opcional. Quando o bem foi comprado ou doado.</span>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faDollarSign} style={{ marginRight: '6px' }} />
                  Valor (R$)
                  <span className="info-hint" data-tip="Valor estimado ou de compra do bem, para fins de controle patrimonial.">
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </label>
                <input
                  type="number"
                  name="value"
                  className="form-input"
                  value={formData.value}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  disabled={saving}
                />
                <span className="field-hint">Opcional. Valor de compra ou estimado.</span>
              </div>
            </div>
          </div>

          {/* Upload progress */}
          {saving && uploadProgress > 0 && (
            <div className="upload-progress-section">
              <ProgressBar progress={uploadProgress} />
              <p className="progress-text">
                <FontAwesomeIcon icon={faCloudUploadAlt} style={{ marginRight: '6px' }} />
                Enviando... {uploadProgress}%
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="form-actions-bar">
            <button
              type="button"
              onClick={() => navigate(`/ministry/${ministryId}/assets`)}
              className="btn btn-secondary"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
            >
              {saving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                  {isEditing ? 'Salvando...' : 'Cadastrando...'}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={isEditing ? faSave : faCheckCircle} style={{ marginRight: '6px' }} />
                  {isEditing ? 'Salvar Alterações' : 'Cadastrar Bem'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssetForm;
